import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControl,
  FormControlLabel,
  Chip,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import { APIs } from '../utilities/APIs';
import { useAuth } from '../users/AuthContext'; // Import useAuth

const ApplicantNoticesManagement = () => {
  const { makeAuthenticatedRequest } = useAuth(); // Get makeAuthenticatedRequest
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'INFO',
    targetPages: ['LOGIN'],
    priority: 1,
    isActive: true
  });

  const noticeTypes = [
    { value: 'INFO', label: 'Information', color: 'info' },
    { value: 'WARNING', label: 'Warning', color: 'warning' },
    { value: 'ERROR', label: 'Error', color: 'error' },
    { value: 'SUCCESS', label: 'Success', color: 'success' }
  ];

  const pageOptions = [
    { value: 'LOGIN', label: 'Login Page' },
    { value: 'REGISTER', label: 'Register Page' },
    { value: 'DASHBOARD', label: 'Dashboard Page' }
  ];

  useEffect(() => {
    fetchNotices();
  }, []);
  const fetchNotices = async () => {
    try {
      setLoading(true);
      const data = await makeAuthenticatedRequest(APIs.notices.admin.all, { // Use makeAuthenticatedRequest
        method: 'GET',
      });
      
      if (data.status === 'success' && data.data) {
        setNotices(data.data);
      } else {
        throw new Error(data.message || `HTTP error! status: ${data.status}`);
      }
    } catch (error) {
      console.error('Error fetching notices:', error);
      setError('Failed to fetch notices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (notice = null) => {
    if (notice) {
      setEditingNotice(notice);      setFormData({
        title: notice.title,
        content: notice.content,
        type: notice.noticeType || notice.type || 'INFO', // Handle both field names
        targetPages: notice.targetPages || ['LOGIN'],
        priority: notice.priority,
        isActive: notice.isActive
      });
    } else {
      setEditingNotice(null);
      setFormData({
        title: '',
        content: '',
        type: 'INFO',
        targetPages: ['LOGIN'],
        priority: 1,
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNotice(null);
    setFormData({
      title: '',
      content: '',
      type: 'INFO',
      targetPages: ['LOGIN'],
      priority: 1,
      isActive: true
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTargetPagesChange = (event) => {
    setFormData(prev => ({
      ...prev,
      targetPages: event.target.value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        setError('Title and content are required');
        return;
      }      const noticeData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        noticeType: formData.type, // Map frontend 'type' to backend 'noticeType'
        targetPages: formData.targetPages,
        priority: formData.priority,
        createdBy: "admin", // TODO: Get from actual user context
        isActive: formData.isActive
      };
      console.log('Submitting noticeData:', noticeData); // Log noticeData
      let data;
      if (editingNotice) {
        data = await makeAuthenticatedRequest(APIs.notices.admin.update(editingNotice.id), { // Use makeAuthenticatedRequest
          method: 'PUT',
          body: JSON.stringify(noticeData),
        });
      } else {
        data = await makeAuthenticatedRequest(APIs.notices.admin.create, { // Use makeAuthenticatedRequest
          method: 'POST',
          body: JSON.stringify(noticeData),
        });
      }

      if (data.status === 'success') {
        setSuccess(editingNotice ? 'Notice updated successfully' : 'Notice created successfully');
        handleCloseDialog();
        fetchNotices();
      } else {
        throw new Error(data.message || `HTTP error! status: ${data.status}`);
      }
    } catch (error) {
      console.error('Error saving notice:', error);
      setError('Failed to save notice: ' + error.message);
    }
  };
  const handleDelete = async (noticeId) => {
    if (!window.confirm('Are you sure you want to delete this notice?')) {
      return;
    }

    try {
      const data = await makeAuthenticatedRequest(APIs.notices.admin.delete(noticeId), { // Use makeAuthenticatedRequest
        method: 'DELETE',
      });
      if (data.status === 'success') {
        setSuccess('Notice deleted successfully');
        fetchNotices();
      } else {
        throw new Error(data.message || `HTTP error! status: ${data.status}`);
      }
    } catch (error) {
      console.error('Error deleting notice:', error);
      setError('Failed to delete notice: ' + error.message);
    }
  };
  const handleToggleStatus = async (notice) => {
    try {
      const data = await makeAuthenticatedRequest(APIs.notices.admin.toggleStatus(notice.id), { // Use makeAuthenticatedRequest
        method: 'PUT',
      });
      if (data.status === 'success') {
        setSuccess(`Notice ${!notice.isActive ? 'activated' : 'deactivated'} successfully`);
        fetchNotices();
      } else {
        throw new Error(data.message || `HTTP error! status: ${data.status}`);
      }
    } catch (error) {
      console.error('Error updating notice status:', error);
      setError('Failed to update notice status: ' + error.message);
    }
  };

  const getTypeChip = (type) => {
    const typeConfig = noticeTypes.find(t => t.value === type);
    return (
      <Chip 
        label={typeConfig?.label || type} 
        color={typeConfig?.color || 'default'}
        size="small"
      />
    );
  };

  const formatTargetPages = (pages) => {
    return pages?.map(page => {
      const pageConfig = pageOptions.find(p => p.value === page);
      return pageConfig?.label || page;
    }).join(', ') || 'N/A';
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading notices...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Applicant Notices Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Notice
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Target Pages</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notices.map((notice) => (
              <TableRow key={notice.id}>
                <TableCell>
                  <Typography variant="subtitle2">
                    {notice.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {notice.content.length > 100 
                      ? `${notice.content.substring(0, 100)}...` 
                      : notice.content
                    }
                  </Typography>
                </TableCell>
                <TableCell>{getTypeChip(notice.type)}</TableCell>
                <TableCell>{formatTargetPages(notice.targetPages)}</TableCell>
                <TableCell>{notice.priority}</TableCell>
                <TableCell>
                  <Chip 
                    label={notice.isActive ? 'Active' : 'Inactive'}
                    color={notice.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(notice.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={notice.isActive ? 'Deactivate' : 'Activate'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(notice)}
                      >
                        {notice.isActive ? <HideIcon /> : <ViewIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(notice)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(notice.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {notices.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    No notices found. Create your first notice to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Notice Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNotice ? 'Edit Notice' : 'Add New Notice'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Content"
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              fullWidth
              multiline
              rows={4}
              required
            />

            <FormControl fullWidth>
              <InputLabel>Notice Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Notice Type"
              >
                {noticeTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Target Pages</InputLabel>
              <Select
                multiple
                value={formData.targetPages}
                onChange={handleTargetPagesChange}
                label="Target Pages"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const pageConfig = pageOptions.find(p => p.value === value);
                      return (
                        <Chip 
                          key={value} 
                          label={pageConfig?.label || value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {pageOptions.map((page) => (
                  <MenuItem key={page.value} value={page.value}>
                    {page.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1, max: 10 }}
              helperText="Priority determines display order (1 = highest priority)"
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingNotice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApplicantNoticesManagement;
